import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSATConnection } from '../../useSATConnection';
import { showAlert } from '../../../../redux/slices/alert.slice';
import { playHaptic } from '../../../../shared/utils/haptics';
import type { RootState } from '../../../../redux/store';
import { ALERT_TYPE } from '../../../../components/molecules/Alert';
import type { LinkingMethod } from '../../useLinkingWorkflow';
import type { LinkingOrigin } from '../../linkingOrigin';
import type { BindErrorCode } from '../bindErrors';
import type { RfcPrefillTracking } from '../../../../shared/fiscal/lib/rfcPrefill';
import { useLinkingTracking } from './useLinkingTracking';
import { useLinkingUrlSync } from './useLinkingUrlSync';

export type LinkingFlowSubmitData = {
	rfc: string;
	password: string;
	keyFile: File;
	cerFile: File;
	efirmaPassword: string;
	rfcPrefillTracking?: RfcPrefillTracking;
};

type ModalFailedState = {
	errorMessage: string;
	errorCode?: BindErrorCode | null;
};

export type UseSATLinkingOptions = {
	/**
	 * Desde qué pantalla se está vinculando. Obligatorio y sin default: ver
	 * `linkingOrigin`. Once superficies pueden vincular y hasta hoy ninguna
	 * dejaba rastro de cuál fue.
	 */
	origin: LinkingOrigin;
	/**
	 * Called when polling completes successfully. When omitted, the standalone
	 * /sat-linking flow falls back to `window.location.reload()` to refresh app
	 * state with the newly linked SAT. Inline callers (onboarding sub-steps that
	 * embed LinkingFlow) pass this to advance their own flow instead.
	 */
	onCompleted?: () => void;
	/**
	 * Opens directly on the credentials step (2) for this method, skipping the
	 * method picker. For surfaces committed to one method (consult-RFC → link).
	 */
	initialMethod?: LinkingMethod;
	/**
	 * Mirror the credentials step into `?linking_step`/`?linking_method`. On by
	 * default; embedded onboarding surfaces pass `false` (nothing consumes those
	 * params there and only ensucian la URL).
	 */
	syncUrl?: boolean;
	relink?: boolean;
};

/**
 * Orquestador del flujo de vinculación SAT. Compone los concerns (tracking, URL
 * sync, last-cart) sobre el estado centralizado del SATConnectionProvider. La
 * UI solo consume la forma de retorno; el motor (useLinkingWorkflow) vive en el
 * provider. Antes era `useLinkingFlowController` (god-hook) — partido por
 * composición sin cambiar su contrato.
 */
export const useSATLinking = (options: UseSATLinkingOptions) => {
	const dispatch = useDispatch();
	const userProfile = useSelector((state: RootState) => state.profile.user);
	const sat = useSATConnection();
	const tracking = useLinkingTracking();

	const [step, setStep] = useState(options.initialMethod ? 2 : 1);
	const [linkingOptionSelected, setLinkingOptionSelected] =
		useState<LinkingMethod>(options.initialMethod ?? 'link');
	const [showModalFailed, setShowModalFailed] = useState<ModalFailedState>({
		errorMessage: '',
	});

	useLinkingUrlSync({
		step,
		linkingOptionSelected,
		syncUrl: options.syncUrl !== false,
	});

	// Mirror onCompleted into a ref so the completion effect calls the latest
	// callback without taking `options` (fresh object every render) as a dep.
	const onCompletedRef = useRef(options.onCompleted);
	onCompletedRef.current = options.onCompleted;

	const { trackPageView, trackCompleted, trackFailed, trackCancelled } =
		tracking;

	// Page view on mount.
	useEffect(() => {
		trackPageView(sat.method, sat.workflowId ?? null);
		// only on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Al retomar desde storage, el método seleccionado tiene que ser el que el
	// workflow restauró, no el default del montaje.
	//
	// `polling` es para no caminar el paso 1/2 con una vinculación en vuelo.
	// `failed` es lo que hace VISIBLE el error: el paso 2 esconde el mensaje
	// cuando el método del fallo guardado no coincide con el seleccionado
	// (`errorMatchesMethod`), y tras una recarga el seleccionado volvía al
	// default. Un usuario que fallaba con e.firma y recargaba aterrizaba en el
	// formulario del otro método con el aviso tragado: fallo mudo.
	//
	// No pisa una elección posterior del usuario: las deps sólo cambian al
	// resolverse el workflow, así que cambiar de método a mano no vuelve a
	// disparar este efecto.
	useEffect(() => {
		if (sat.workflowPhase === 'polling' || sat.workflowPhase === 'failed') {
			setLinkingOptionSelected(sat.method);
		}
	}, [sat.workflowPhase, sat.method]);

	// Phase transitions (completed / polling-failed). Submit-time failures are
	// handled inline by handleSubmit so they don't double-fire here.
	const prevPhaseRef = useRef(sat.workflowPhase);
	useEffect(() => {
		const prev = prevPhaseRef.current;
		prevPhaseRef.current = sat.workflowPhase;

		if (
			(prev === 'polling' || prev === 'submitting') &&
			sat.workflowPhase === 'completed'
		) {
			trackCompleted(
				sat.method,
				sat.workflowId ?? null,
				sat.rfcPrefillTracking,
			);
			// Éxito y fallo viven en el mismo punto: la transición de fase es la
			// determinación central de la vinculación. El háptico no vive en el
			// SuccessLinkingModal (componente) sino aquí, junto a su contraparte
			// de error (abajo), para que el feedback sea consistente.
			playHaptic('success');
			if (onCompletedRef.current) {
				onCompletedRef.current();
				return;
			}
			window.location.reload();
			return;
		}
		if (
			(prev === 'polling' || prev === 'submitting') &&
			sat.workflowPhase === 'failed'
		) {
			const errorMessage = sat.errorMessage ?? '';
			trackFailed(
				sat.method,
				errorMessage,
				sat.errorCode,
				sat.rfcPrefillTracking,
			);
			playHaptic('error');
			setShowModalFailed({ errorMessage, errorCode: sat.errorCode });
			setLinkingOptionSelected(sat.method);
			setStep(2);
		}
	}, [
		sat.workflowPhase,
		sat.method,
		sat.workflowId,
		sat.errorMessage,
		sat.errorCode,
		sat.rfcPrefillTracking,
		trackCompleted,
		trackFailed,
	]);

	const handleSelectMethod = (option: LinkingMethod) => {
		setLinkingOptionSelected(option);
		setStep(2);
	};

	const handleSubmit = async (data: LinkingFlowSubmitData) => {
		if (!userProfile?.id) return;

		setShowModalFailed({ errorMessage: '' });

		if (
			linkingOptionSelected === 'efirma' &&
			(!data.cerFile || !data.keyFile)
		) {
			dispatch(
				showAlert({
					title: 'Error',
					type: ALERT_TYPE.ERROR,
					description: 'Por favor selecciona ambos archivos (.cer y .key)',
				}),
			);
			return;
		}

		const rfcPrefillTracking =
			linkingOptionSelected === 'link' ? data.rfcPrefillTracking : undefined;
		const result =
			linkingOptionSelected === 'link'
				? await sat.submitLink({
						userId: userProfile.id,
						rfc: data.rfc,
						password: data.password,
						origin: options.origin,
						rfcPrefillTracking,
						relink: options.relink,
					})
				: await sat.submitEfirmaForLogin({
						userId: userProfile.id,
						cerFile: data.cerFile,
						keyFile: data.keyFile,
						efirmaPassword: data.efirmaPassword,
						origin: options.origin,
						relink: options.relink,
					});

		tracking.trackSubmitOutcome(
			linkingOptionSelected,
			result,
			rfcPrefillTracking,
		);

		// El fallo no abre avisos automáticos: el banner fiscal se pone rojo y
		// las superficies activas (form inline, SatLinkingDrawer) muestran el
		// motivo leyendo este estado / el del workflow.
		if (!result.ok) {
			playHaptic('error');
			setShowModalFailed({ errorMessage: result.errorMessage });
		}
	};

	const handleCancel = async () => {
		trackCancelled(sat.method, sat.workflowId ?? null);
		await sat.cancel();
		setStep(1);
	};

	return {
		step,
		linkingOptionSelected,
		setLinkingOptionSelected,
		showModalFailed,
		setShowModalFailed,
		setStep,
		isPolling: sat.isLinkingInFlight,
		isLinked: sat.isLinked,
		isLoading: sat.workflowPhase === 'submitting',
		handleSelectMethod,
		handleSubmit,
		handleCancel,
	};
};
